/**
 * AlertSystem - Main alert manager component for GRIND TIME
 * Handles different types of alerts: toasts, banners, celebrations
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Trophy, MessageCircle, CloudRain, Bell, TrendingUp, AlertTriangle, CheckCircle, Star, Zap } from 'lucide-react';
import WeatherBanner from './WeatherBanner';
import LeaderboardCelebration from './LeaderboardCelebration';

// Alert polling interval (30 seconds)
const POLL_INTERVAL = 30000;

const AlertSystem = () => {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [weatherAlert, setWeatherAlert] = useState(null);
  const [celebrationAlert, setCelebrationAlert] = useState(null);
  const [shownAlertIds, setShownAlertIds] = useState(new Set());

  // Fetch alerts from API
  const fetchAlerts = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch regular alerts
      const alertsRes = await axios.get(`${API}/alerts`, { headers });
      setAlerts(alertsRes.data || []);
      
      // Fetch weather alert
      const weatherRes = await axios.get(`${API}/alerts/weather`, { headers });
      setWeatherAlert(weatherRes.data);
      
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  }, [token]);

  // Initial fetch and polling
  useEffect(() => {
    // Using IIFE to properly handle async in useEffect
    const init = async () => {
      await fetchAlerts();
    };
    init();
    const interval = setInterval(fetchAlerts, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Dismiss an alert
  const dismissAlert = useCallback(async (alertId) => {
    if (!token) return;
    
    try {
      await axios.put(`${API}/alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    }
  }, [token]);

  // Show toast notification with custom styling
  const showToastAlert = useCallback((alert) => {
    const iconMap = {
      'message-circle': MessageCircle,
      'trophy': Trophy,
      'trending-up': TrendingUp,
      'bell': Bell,
      'cloud-rain': CloudRain,
      'alert-triangle': AlertTriangle,
      'check-circle': CheckCircle,
      'star': Star,
      'zap': Zap,
    };
    
    const Icon = iconMap[alert.icon] || Bell;
    
    const severityStyles = {
      info: { bg: 'bg-neon-blue/20', border: 'border-neon-blue/50', text: 'text-neon-blue' },
      success: { bg: 'bg-green-500/20', border: 'border-green-500/50', text: 'text-green-400' },
      warning: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', text: 'text-yellow-400' },
      danger: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400' },
      celebration: { bg: 'bg-neon-pink/20', border: 'border-neon-pink/50', text: 'text-neon-pink' },
    };
    
    const style = severityStyles[alert.severity] || severityStyles.info;
    
    toast.custom((t) => (
      <div 
        className={`${style.bg} ${style.border} border rounded-lg p-4 shadow-lg backdrop-blur-sm max-w-md animate-slide-in-right`}
        data-testid={`alert-toast-${alert.id}`}
      >
        <div className="flex items-start gap-3">
          <div className={`${style.text} mt-0.5`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-orbitron text-sm font-bold ${style.text}`}>
              {alert.title}
            </p>
            <p className="font-rajdhani text-gray-300 text-sm mt-1 line-clamp-2">
              {alert.message}
            </p>
          </div>
          {alert.is_dismissible && (
            <button 
              onClick={() => {
                toast.dismiss(t);
                dismissAlert(alert.id);
              }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    ), {
      duration: 8000,
      position: 'top-right',
    });
  }, [dismissAlert]);

  // Process new alerts and show them
  useEffect(() => {
    alerts.forEach(alert => {
      if (shownAlertIds.has(alert.id)) return;
      
      // Mark as shown
      setShownAlertIds(prev => new Set([...prev, alert.id]));
      
      // Handle different display styles
      if (alert.display_style === 'celebration') {
        setCelebrationAlert(alert);
      } else if (alert.display_style === 'toast') {
        showToastAlert(alert);
      }
    });
  }, [alerts, shownAlertIds, showToastAlert]);

  // Close celebration modal
  const closeCelebration = () => {
    if (celebrationAlert) {
      dismissAlert(celebrationAlert.id);
    }
    setCelebrationAlert(null);
  };

  return (
    <>
      {/* Weather Banner - Fixed at top */}
      {weatherAlert && weatherAlert.is_active && (
        <WeatherBanner alert={weatherAlert} />
      )}
      
      {/* Leaderboard Celebration Modal */}
      {celebrationAlert && (
        <LeaderboardCelebration 
          alert={celebrationAlert} 
          onClose={closeCelebration}
        />
      )}
    </>
  );
};

export default AlertSystem;
